import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import type { Language } from '@/types/language';
import { getCurrentUser, updateLanguagePreference } from '@/lib/api';
import { changeLanguage as i18nChangeLanguage } from '@/lib/i18n';

interface LanguageSelectorProps {
  className?: string;
}

export function LanguageSelector({ className }: LanguageSelectorProps) {
  const { t, i18n } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  const { toast } = useToast();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const LANGUAGES = useMemo(() => ({
    en: { name: t('common.languageselector.english', 'English'), nativeName: t('common.languageselector.english', 'English'), flag: '🇬🇧' },
    es: { name: t('common.languageselector.spanish', 'Spanish'), nativeName: 'Español', flag: '🇪🇸' },
    fr: { name: t('common.languageselector.french', 'French'), nativeName: 'Français', flag: '🇫🇷' },
    de: { name: t('common.languageselector.german', 'German'), nativeName: t('common.languageselector.deutsch', 'Deutsch'), flag: '🇩🇪' },
    pt: { name: t('common.languageselector.portuguese', 'Portuguese'), nativeName: 'Português', flag: '🇵🇹' },
    it: { name: t('common.languageselector.italian', 'Italian'), nativeName: t('common.languageselector.italiano', 'Italiano'), flag: '🇮🇹' },
  }), [t]);

  // Get current language from i18n
  const currentLanguage = (i18n.language as Language) || 'en';

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const updateLanguage = useCallback(async (language: Language) => {
    console.log('LanguageSelector: updateLanguage called with', language);
    
    if (language === currentLanguage) {
      console.log('LanguageSelector: Language is the same, no update needed');
      setIsOpen(false);
      return;
    }

    try {
      console.log('LanguageSelector: Updating language to', language);
      
      // 1. Update i18n language (this also updates localStorage and dispatches event)
      await i18nChangeLanguage(language);
      console.log('LanguageSelector: i18n language updated');
      
      // 2. Update in database if user is logged in
      const userResult = await getCurrentUser();
      if (userResult.success) {
        console.log('LanguageSelector: Updating language in database for user', userResult.data.id);
        const updateResult = await updateLanguagePreference(userResult.data.id, language);
          
        if (!updateResult.success) {
          const errorMsg = 'error' in updateResult ? updateResult.error.message : t('common.languageselector.failed_update', 'Failed to update');
          throw new Error(errorMsg);
        }
        console.log('LanguageSelector: Database update successful');
      }
      
      // 3. Show success message
      toast({
        title: t('language.updated.title', 'Language updated'),
        description: t('language.updated.description', { languageName: LANGUAGES[language].name, defaultValue: 'Language changed to {{languageName}}' }),
      });

      // 4. Close dropdown
      setIsOpen(false);
      
    } catch (error) {
      console.error('LanguageSelector: Error updating language:', error);
      toast({
        title: t('language.updateError.title', 'Failed to update language'),
        description: t('language.updateError.description', 'Please try again or contact support if the problem persists.'),
        variant: "destructive",
      });
    }
  }, [currentLanguage, toast, t]);

  const currentLang = LANGUAGES[currentLanguage] || LANGUAGES.en;

  return (
    <div className={`relative inline-block ${className || ''}`} ref={dropdownRef}>
      <button
        type="button"
        className="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="text-base">{currentLang.flag}</span>
        <span className="hidden sm:inline">{currentLang.nativeName}</span>
        <span className="sm:hidden">{currentLanguage.toUpperCase()}</span>
        <ChevronDown className="w-4 h-4 ml-1" />
      </button>

      {isOpen && (
        <div
          className="absolute right-0 z-50 w-48 mt-2 origin-top-right bg-white rounded-md shadow-lg dark:bg-gray-800 ring-1 ring-black ring-opacity-5 focus:outline-none"
          role="listbox"
          aria-label={t('language.selector.ariaLabel', 'Language selection')}
        >
          <div className="py-1">
            {Object.entries(LANGUAGES).map(([code, lang]) => (
              <button
                key={code}
                type="button"
                className={`flex items-center w-full px-4 py-2 text-sm text-left ${
                  currentLanguage === code
                    ? 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white'
                    : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700'
                }`}
                onClick={() => updateLanguage(code as Language)}
                role="option"
                aria-selected={currentLanguage === code}
              >
                <span className="mr-2 text-base">{lang.flag}</span>
                <span>{lang.nativeName}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default LanguageSelector;
